import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { CSeverity, NativeProps } from '@/src/components/interfaces';
import { CFlex, FlexDirection } from '@/src/components/c-flex/CFlex';
import { CFieldLabel, CFieldSecurity } from '@/src/components/c-field-label/CFieldLabel';
import { CFieldValidation, FieldValidation } from '@/src/components/c-field-validations/CFieldValidation';
import { CIcon, CIcons } from '@/src/components/c-icon/CIcon';
import { guid } from '@/src/infrastructure/guid/guid';
import { CFieldAutoCapitalize, CFieldAutoCorrect, CFieldEnterKeyHint, CFieldInputMode } from '@/src';

export interface CNumberFieldProps extends NativeProps {
  value?: number;
  onChange: (value: number | undefined) => void;
  step?: number;
  min?: number;
  max?: number;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  security?: CFieldSecurity;
  validations?: FieldValidation[];
  hideMessageContainer?: boolean;
  inputMode?: CFieldInputMode.Numeric | CFieldInputMode.Decimal;
  enterKeyHint?: CFieldEnterKeyHint;
}

export function CNumberField(props: CNumberFieldProps): React.JSX.Element {
  const [id] = useState(() => guid('number-field'));
  const step = props.step ?? 1;
  const inputMode = props.inputMode ?? CFieldInputMode.Numeric;

  const status = useMemo(() => {
    const hasErrors = props.validations?.some(v => v.severity === CSeverity.Error) ?? false;
    const hasWarnings = props.validations?.some(v => v.severity === CSeverity.Warning) ?? false;
    if (hasErrors) return 'error';
    if (hasWarnings) return 'warning';
    return 'neutral';
  }, [props.validations]);

  const clamp = useCallback((val: number) => {
    let result = val;
    if (props.min !== undefined) result = Math.max(props.min, result);
    if (props.max !== undefined) result = Math.min(props.max, result);
    return result;
  }, [props.min, props.max]);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      props.onChange(undefined);
      return;
    }
    const parsed = parseFloat(raw);
    if (!isNaN(parsed)) {
      props.onChange(clamp(parsed));
    }
  }, [props.onChange, clamp]);

  const handleDecrement = useCallback(() => {
    const current = props.value ?? 0;
    props.onChange(clamp(current - step));
  }, [props.value, props.onChange, step, clamp]);

  const handleIncrement = useCallback(() => {
    const current = props.value ?? 0;
    props.onChange(clamp(current + step));
  }, [props.value, props.onChange, step, clamp]);

  const isMinReached = props.min !== undefined && (props.value ?? 0) <= props.min;
  const isMaxReached = props.max !== undefined && (props.value ?? 0) >= props.max;

  return (
    <CFlex
      id={props.id}
      direction={FlexDirection.Column}
      class={classNames('c-number-field', {
        'c-number-field--disabled': props.disabled,
        'c-number-field--full-width': props.fullWidth,
        [`c-number-field--status-${status}`]: status !== 'neutral',
      }, props.class)}
      style={props.style}
    >
      {props.label && (
        <CFieldLabel htmlFor={`${id}-input`} label={props.label} required={props.required} security={props.security}/>
      )}

      <div className="c-number-field__container">
        <button
          type="button"
          className={classNames('c-number-field__btn', { 'c-number-field__btn--disabled': isMinReached })}
          onClick={handleDecrement}
          disabled={props.disabled || isMinReached}
          aria-label="Decrease"
        >
          <CIcon icon={CIcons.Minus}/>
        </button>
        <input
          id={`${id}-input`}
          className="c-number-field__input"
          type="number"
          step={step}
          min={props.min}
          max={props.max}
          value={props.value ?? ''}
          placeholder={props.placeholder}
          disabled={props.disabled}
          required={props.required}
          onChange={handleChange}
          inputMode={inputMode}
          enterKeyHint={props.enterKeyHint}
          autoCapitalize={CFieldAutoCapitalize.Off}
          autoCorrect={CFieldAutoCorrect.Off}
        />
        <button
          type="button"
          className={classNames('c-number-field__btn', { 'c-number-field__btn--disabled': isMaxReached })}
          onClick={handleIncrement}
          disabled={props.disabled || isMaxReached}
          aria-label="Increase"
        >
          <CIcon icon={CIcons.Plus}/>
        </button>
      </div>

      {props.validations && props.validations.length > 0 && !props.hideMessageContainer && (
        <CFieldValidation validations={props.validations}/>
      )}
    </CFlex>
  );
}
